import { useEffect, useRef } from "react";
import type { RefObject } from "react";

import { NavItem } from "@/components/shell/NavItem";
import { useCurrentActor } from "@/lib/current-actor";
import { MORE_SHEET_DESTINATIONS } from "@/lib/destinations";
import { useOverlayGrant } from "@/lib/overlay-gate";
import { useSignOut } from "@/lib/use-sign-out";

/**
 * The phone "Menu" sheet (Story 20.1 relabeled "More" → "Menu"; this
 * component's own name and the destinations it holds are unchanged),
 * holding the destinations that don't fit the bottom bar's four slots
 * (DESIGN.md § Layout & Spacing) — five by the original design, currently
 * two ("Documents," "Vendors") since Story 15.14 filters "Bills &
 * Compliance," "Money" and "Work orders" out of `MORE_SHEET_DESTINATIONS`
 * while they're still pure placeholder stubs, and Story 20.1 additionally
 * hides "Settings" now that a header gear icon reaches it directly.
 * Built as a plain
 * positioned panel with a scrim and a manual focus trap rather than on
 * `Dialog`/`Sheet.tsx` (Radix `Dialog`) — this predates Story 2.6, which is
 * the story that actually resolved the Radix-vs-hand-built question in
 * Radix's favour (`epics.md`'s dated 2026-08-17 amendment, referenced from
 * `2-6-dialogs-confirmations-toasts.md`'s own Intent). Left hand-built
 * rather than swapped onto `Sheet.tsx` in the same pass — out of Story
 * 2.6's own Code Map, which names retrofitting the two deferred row
 * actions, not re-platforming this pre-existing one — but it registers
 * with the same shared overlay gate (`lib/overlay-gate.ts`) below, so "no
 * overlay opens on top of another" is a structural property of every
 * overlay in the app, hand-built or Radix-based alike, not only the four
 * this story added.
 *
 * Gap-fill (frontend auth UI) — this is also the phone sign-out surface.
 * `NavRail` (the desktop rail) carries the sign-out control, but `NavRail`
 * itself is `md:hidden` — on phone it never renders at all, so without this
 * a phone-only visitor would have no way to sign out anywhere in the app.
 * Shares `useSignOut` with `NavRail` rather than duplicating the call.
 */
export function MoreSheet({
  open,
  onClose,
  triggerRef,
}: {
  open: boolean;
  onClose: () => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const granted = useOverlayGrant(open);
  const { data: actor } = useCurrentActor();
  const { isSigningOut, signOut } = useSignOut();

  useEffect(() => {
    if (!granted) {
      return;
    }

    const panel = panelRef.current;
    const focusable = panel?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
    focusable?.[0]?.focus();

    // `aria-modal="true"` on the panel is a hint, not an enforcement — a
    // screen reader's virtual cursor can still wander into the content
    // behind the scrim unless that content is explicitly hidden from the
    // accessibility tree while the sheet is open (UX-DR20).
    const main = document.querySelector("main");
    main?.setAttribute("aria-hidden", "true");

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !focusable || focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      main?.removeAttribute("aria-hidden");
      triggerRef.current?.focus();
    };
  }, [granted, onClose, triggerRef]);

  if (!granted) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 md:hidden">
      <button type="button" aria-label="Close the Menu panel" onClick={onClose} className="absolute inset-0 bg-scrim" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        className="absolute inset-x-0 bottom-0 rounded-t-lg bg-surface p-5 shadow-overlay"
      >
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-border-strong" aria-hidden="true" />
        <nav aria-label="Menu">
          <ul className="flex flex-col gap-1">
            {MORE_SHEET_DESTINATIONS.map((destination) => (
              <li key={destination.id}>
                <NavItem destination={destination} layout="sheet" onNavigate={onClose} />
              </li>
            ))}
          </ul>
        </nav>
        {actor && (
          <div className="mt-3 flex items-center justify-between gap-2 border-t border-border-hairline px-1 pt-3">
            <p className="text-body-strong font-medium text-ink">{actor.display_name}</p>
            <button
              type="button"
              onClick={() => void signOut()}
              disabled={isSigningOut}
              className="text-meta text-ink-secondary underline hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSigningOut ? "Signing out…" : "Sign out"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
